import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PasswordInput from '../../components/PasswordInput';
import { useAuth } from '../../context/AuthContext';

export default function AdminUsers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', roleId: '' });
  const [showForm, setShowForm] = useState(false);
  const isSuperAdmin = user?.role?.isSystem === true;
  const assignableRoles = isSuperAdmin ? roles : roles.filter((role) => !role.isSystem);

  const fetchAll = () => {
    api.get('/users/admins').then((res) => setAdmins(res.data));
    api.get('/roles').then((res) => setRoles(res.data));
  };

  useEffect(fetchAll, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/admins', form);
      toast.success('Admin account created and emailed');
      setForm({ name: '', email: '', password: '', roleId: '' });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create admin');
    }
  };

  const changeRole = async (id, roleId) => {
    try {
      await api.put(`/users/admins/${id}`, { roleId });
      toast.success('Role updated');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update role');
    }
  };

  const toggleActive = async (admin) => {
    try {
      await api.put(`/users/admins/${admin._id}`, { isActive: !admin.isActive });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update status');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this admin user?')) return;
    try {
      await api.delete(`/users/admins/${id}`);
      toast.success('Admin removed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove admin');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl text-primaryDark">Admin Users</h1>
        <button onClick={() => setShowForm((s) => !s)} className="btn-primary text-sm">
          {showForm ? 'Close' : '+ Add Admin'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="card p-6 mb-6 grid md:grid-cols-4 gap-4 items-end max-w-4xl">
          <div>
            <label className="label">Name</label>
            <input required className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label">Email</label>
            <input required type="email" className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label">Temporary Password</label>
            <PasswordInput required autoComplete="new-password" minLength="6" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div>
            <label className="label">Role</label>
            <select required className="input-field" value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
              <option value="">Select role</option>
              {assignableRoles.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
          <button className="btn-dark md:col-span-4">Create Admin Account</button>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-primaryDark/5 text-left">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a._id} className="border-t border-primaryBrown/10">
                <td className="p-3">{a.name}</td>
                <td className="p-3">{a.email}</td>
                <td className="p-3">
                  {a.role?.isSystem ? (
                    <span>{a.role.name}</span>
                  ) : (
                    <select className="input-field py-1 text-xs" value={a.role?._id || ''} onChange={(e) => changeRole(a._id, e.target.value)}>
                      <option value="">No role</option>
                      {assignableRoles.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
                    </select>
                  )}
                </td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${a.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {a.isActive ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="p-3 space-x-3">
                  {a._id !== user?._id && (!a.role?.isSystem || isSuperAdmin) && (
                    <button onClick={() => toggleActive(a)} className="text-accentGold hover:underline">
                      {a.isActive ? 'Disable' : 'Enable'}
                    </button>
                  )}
                  {a._id !== user?._id && (!a.role?.isSystem || isSuperAdmin) && (
                    <button onClick={() => remove(a._id)} className="text-red-600 hover:underline">Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
